import type { ButtonHTMLAttributes, ReactNode } from "react";
import "./Button.css";

export type ButtonVariant =
  | "primary"
  | "accent"
  | "secondary"
  | "outline"
  | "danger"
  /** amber fill: a warning you can click, e.g. "13 documents cannot be analyzed" */
  | "warning"
  /** red text/icon, no border: delete-row icons */
  | "dangerGhost"
  | "ghost"
  | "link";

export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** full width of its container */
  block?: boolean;
  /** shows a spinner and blocks clicks */
  loading?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  /** round button with only an icon (pass it as iconStart and an aria-label) */
  iconOnly?: boolean;
}

/**
 * The one button. Pill shape, three heights (32 / 44 / 52).
 * Example: <Button variant="primary" size="lg" block onClick={save}>Save</Button>
 */
const Button = ({
  variant = "primary",
  size = "md",
  block = false,
  loading = false,
  iconStart,
  iconEnd,
  iconOnly = false,
  type = "button",
  className = "",
  disabled,
  children,
  ...rest
}: ButtonProps) => (
  <button
    type={type}
    className={[
      "ds-btn",
      `ds-btn--${variant}`,
      `ds-btn--${size}`,
      block ? "ds-btn--block" : "",
      iconOnly ? "ds-btn--icon" : "",
      loading ? "is-loading" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    {...rest}
  >
    {loading ? <span className="ds-spinner" aria-hidden="true" /> : iconStart}
    {children}
    {!loading && iconEnd}
  </button>
);

export default Button;
